import React, { useState, useRef } from 'react';
import { StatsFilterModal } from '../../../components/coach/dashboard/StatsFilterModal';
import scoutProfileService from '../../../services/scoutProfileService';
import { normalizePlayerClass } from '../../../utils/normalizePlayerClass';

export const StatsTable = ({ activeTab, followedPlayers = [] }) => {
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedStatColumns, setSelectedStatColumns] = useState([]);
  const buttonRef = useRef(null);

  // Define stat column mappings
  const statColumnMapping = {
    // Hitting stats
    'avg': { label: 'AVG', key: 'batting_average' },
    'obp': { label: 'OBP', key: 'on_base_percentage' },
    'slg': { label: 'SLG', key: 'slugging_percentage' },
    'ops': { label: 'OPS', key: 'ops', calculated: true },
    'ops+': { label: 'OPS+', key: 'ops_plus' },
    'runs': { label: 'R', key: 'runs' },
    'hits': { label: 'H', key: 'hits' },
    '2b': { label: '2B', key: 'doubles' },
    '3b': { label: '3B', key: 'triples' },
    'hr': { label: 'HR', key: 'home_runs' },
    'rbi': { label: 'RBI', key: 'rbi' },
    'tb': { label: 'TB', key: 'total_bases' },
    'bb': { label: 'BB', key: 'walks' },
    'k': { label: 'K', key: 'strikeouts' },
    'k%': { label: 'K%', key: 'k_pct', calculated: true },
    'bb%': { label: 'BB%', key: 'bb_pct', calculated: true },
    'k/bb': { label: 'K/BB', key: 'k_bb', calculated: true },
    'sb': { label: 'SB', key: 'stolen_bases' },
    'cs': { label: 'CS', key: 'caught_stealing' },
    'iso': { label: 'ISO', key: 'iso' },
    'woba': { label: 'wOBA', key: 'woba' },
    'wrc+': { label: 'wRC+', key: 'wrc_plus' },
    'pa': { label: 'PA', key: 'pa' },
    'babip': { label: 'BABIP', key: 'babip' },
    // Pitching stats
    'era': { label: 'ERA', key: 'era' },
    'whip': { label: 'WHIP', key: 'whip' },
    'ip': { label: 'IP', key: 'innings_pitched' },
    'so': { label: 'SO', key: 'strikeouts_pitched' },
    'gs': { label: 'GS', key: 'games_started' },
    'app': { label: 'Games', key: 'games_pitched' },
    'cg': { label: 'CG', key: 'complete_games' },
    'sho': { label: 'SHO', key: 'shutouts' },
    'sv': { label: 'SV', key: 'saves' },
    'h': { label: 'H', key: 'hits_allowed' },
    'r': { label: 'R', key: 'runs_allowed' },
    'er': { label: 'ER', key: 'earned_runs' },
    'w': { label: 'W', key: 'wins' },
    'l': { label: 'L', key: 'losses' },
    'fip': { label: 'FIP', key: 'fip' },
    'k-bb%': { label: 'K-BB%', key: 'k_bb_pct' },
    'fip+': { label: 'FIP+', key: 'fip_plus' },
    'era+': { label: 'ERA+', key: 'era_plus' },
    'xfip': { label: 'xFIP', key: 'xfip' },
    'lob%': { label: 'LOB%', key: 'lob_pct' },
    'babip-against': { label: 'BABIP Against', key: 'babip_against' },
    'baa': { label: 'BAA', key: 'batting_average_against' },
    'hbp': { label: 'HBP', key: 'hit_batters' },
  };

  // Default columns (always visible)
  const fixedColumns = ['Name', 'Position', 'Team', 'Class'];

  // Determine dynamic stat columns based on filters
  const getDynamicColumns = () => {
    if (selectedStatColumns.length > 0) {
      return selectedStatColumns.map(col => statColumnMapping[col]).filter(Boolean);
    }

    // Default columns when no filters are selected
    if (activeTab === 'hitting') {
      return [
        { label: 'Games', key: 'games_played' },
        { label: 'AVG', key: 'batting_average' },
        { label: 'OBP', key: 'on_base_percentage' },
        { label: 'SLG', key: 'slugging_percentage' },
        { label: 'OPS', key: 'ops', calculated: true },
        { label: 'R', key: 'runs' },
        { label: 'H', key: 'hits' },
        { label: '2B', key: 'doubles' },
        { label: '3B', key: 'triples' },
        { label: 'HR', key: 'home_runs' },
        { label: 'RBI', key: 'rbi' },
        { label: 'TB', key: 'total_bases' },
        { label: 'BB', key: 'walks' },
        { label: 'K', key: 'strikeouts' },
      ];
    } else {
      return [
        { label: 'W', key: 'wins' },
        { label: 'L', key: 'losses' },
        { label: 'ERA', key: 'era' },
        { label: 'Games', key: 'games_pitched' },
        { label: 'CG', key: 'complete_games' },
        { label: 'SHO', key: 'shutouts' },
        { label: 'SV', key: 'saves' },
        { label: 'IP', key: 'innings_pitched' },
        { label: 'H', key: 'hits_allowed' },
        { label: 'R', key: 'runs_allowed' },
        { label: 'ER', key: 'earned_runs' },
        { label: 'BB', key: 'walks_allowed' },
        { label: 'K', key: 'strikeouts_pitched' },
        { label: 'HR', key: 'home_runs_allowed' },
      ];
    }
  };

  const dynamicColumns = getDynamicColumns();
  const allColumns = [...fixedColumns, ...dynamicColumns.map(col => col.label)];

  const formatStat = (value, columnKey) => {
    if (value === null || value === undefined || value === 0) return '-';

    // Handle MongoDB Decimal128 objects
    if (typeof value === 'object' && value.$numberDecimal) {
      value = parseFloat(value.$numberDecimal);
    }

    if (typeof value === 'number') {
      // OPS+, wRC+ → whole number
      if (columnKey === 'ops_plus' || columnKey === 'wrc_plus') return Math.round(value).toString();
      if (columnKey === 'WHIP' || columnKey === 'whip') return value.toFixed(2);
      return value % 1 === 0 ? value : value.toFixed(3);
    }
    return value;
  };

  const calculateOPS = (obp, slg) => {
    const obpVal = parseFloat(obp) || 0;
    const slgVal = parseFloat(slg) || 0;
    return (obpVal + slgVal).toFixed(3);
  };

  const handleApplyFilters = (filters) => {
    console.log('Applied filters:', filters);

    // Store selected filters for dynamic column display
    if (filters.selectedFilters && filters.selectedFilters.length > 0) {
      setSelectedStatColumns(filters.selectedFilters);
    } else {
      setSelectedStatColumns([]);
    }
  };

  // Helper function to get stat value from player data
  const getStatValue = (player, column) => {
    // Handle array stats (API returns battingStats/pitchingStats as arrays)
    const getBattingStats = (p) => Array.isArray(p.battingStats) ? p.battingStats[0] : p.battingStats;
    const getPitchingStats = (p) => Array.isArray(p.pitchingStats) ? p.pitchingStats[0] : p.pitchingStats;

    const stats = activeTab === 'hitting' ? getBattingStats(player) : getPitchingStats(player);

    // K%/BB% — read directly from backend
    const fmtPct = (v) => {
      if (v === null || v === undefined || v === '') return '0';
      const n = Number(v);
      if (isNaN(n)) return '0';
      return n > 1 ? `${n.toFixed(1)}%` : `${(n * 100).toFixed(1)}%`;
    };
    if (column.calculated && column.key === 'k_pct') {
      const v = activeTab === 'pitching' ? stats?.K_percent : stats?.k_percentage;
      return fmtPct(v);
    }
    if (column.calculated && column.key === 'bb_pct') {
      const v = activeTab === 'pitching' ? stats?.BB_percent : stats?.bb_percentage;
      return fmtPct(v);
    }
    if (column.key === 'k_bb_pct') return fmtPct(stats?.K_BB_percent);
    if (column.key === 'lob_pct') return fmtPct(stats?.LOB_percent);
    if (column.key === 'fip') {
      const v = stats?.FIP ?? stats?.fip;
      return v === null || v === undefined || v === '' ? '0' : Number(v).toFixed(2);
    }
    if (column.key === 'fip_plus') {
      const v = stats?.FIP_plus ?? stats?.fip_plus;
      return v === null || v === undefined || v === '' ? '0' : Math.round(Number(v)).toString();
    }
    if (column.key === 'era_plus') {
      const v = stats?.ERA_plus ?? stats?.era_plus;
      return v === null || v === undefined || v === '' ? '0' : Math.round(Number(v)).toString();
    }
    if (column.key === 'xfip') {
      const v = stats?.xFIP ?? stats?.xfip;
      return v === null || v === undefined || v === '' ? '0' : Number(v).toFixed(2);
    }
    if (column.key === 'babip_against') {
      const v = stats?.BABIP_against ?? stats?.babip_against;
      return v === null || v === undefined || v === '' ? '0' : Number(v).toFixed(3);
    }

    // K/BB = K / BB (strikeouts / walks)
    if (column.calculated && column.key === 'k_bb') {
      const k = parseFloat(stats?.so ?? stats?.strikeouts ?? stats?.strikeouts_pitched ?? 0);
      const bb = parseFloat(stats?.bb ?? stats?.walks ?? stats?.walks_allowed ?? 0);
      if (bb > 0) return (k / bb).toFixed(2);
      return k > 0 ? '∞' : '-';
    }

    if (column.calculated && column.key === 'ops') {
      const obp = stats?.on_base_percentage || stats?.ob_pct || stats?.onBasePercentage;
      const slg = stats?.slugging_percentage || stats?.slg_pct || stats?.sluggingPercentage;
      return calculateOPS(obp, slg);
    }

    // Fallback key mappings for different API response formats
    const fallbackMapping = {
      'batting_average': ['batting_average', 'avg', 'battingAverage'],
      'on_base_percentage': ['on_base_percentage', 'ob_pct', 'onBasePercentage'],
      'slugging_percentage': ['slugging_percentage', 'slg_pct', 'sluggingPercentage'],
      'games_played': ['games_played', 'gp', 'gamesPlayed', 'g'],
      'runs': ['runs', 'r'],
      'hits': ['hits', 'h'],
      'doubles': ['doubles', '2b'],
      'triples': ['triples', '3b'],
      'home_runs': ['home_runs', 'hr', 'homeRuns'],
      'rbi': ['rbi'],
      'total_bases': ['total_bases', 'tb', 'totalBases'],
      'walks': ['walks', 'bb'],
      'strikeouts': ['strikeouts', 'so', 'k'],
      'stolen_bases': ['stolen_bases', 'sb', 'stolenBases'],
      'caught_stealing': ['caught_stealing', 'cs', 'caughtStealing'],
      'era': ['era', 'ERA'],
      'whip': ['whip', 'WHIP'],
      'innings_pitched': ['innings_pitched', 'ip', 'inningsPitched'],
      'strikeouts_pitched': ['strikeouts_pitched', 'so', 'strikeoutsPitched', 'strikeouts'],
      'games_started': ['games_started', 'gs', 'gamesStarted'],
      'games_pitched': ['games_pitched', 'gp', 'gamesPitched', 'appearances'],
      'complete_games': ['complete_games', 'cg', 'completeGames'],
      'shutouts': ['shutouts', 'sho'],
      'saves': ['saves', 'sv'],
      'hits_allowed': ['hits_allowed', 'h', 'hitsAllowed'],
      'runs_allowed': ['runs_allowed', 'r', 'runsAllowed'],
      'earned_runs': ['earned_runs', 'er', 'earnedRuns'],
      'walks_allowed': ['walks_allowed', 'bb', 'walksAllowed'],
      'home_runs_allowed': ['home_runs_allowed', 'hr', 'homeRunsAllowed'],
      'wins': ['wins', 'w'],
      'losses': ['losses', 'l'],
      'fielding_percentage': ['fielding_percentage', 'fpct', 'fieldingPercentage'],
      'errors': ['errors', 'e'],
      'putouts': ['putouts', 'po'],
      'assists': ['assists', 'a'],
      'double_plays': ['double_plays', 'dp', 'doublePlays'],
      'total_chances': ['total_chances', 'tc', 'totalChances'],
      // New advanced stats fallbacks
      'iso': ['iso', 'isolatedPower', 'isolated_power'],
      'woba': ['woba', 'wOBA', 'weighted_on_base_average'],
      'wrc_plus': ['wRC_plus', 'wrc_plus', 'wRC+', 'wrcPlus', 'weighted_runs_created_plus'],
      'pa': ['pa', 'plate_appearances', 'plateAppearances'],
      'babip': ['babip', 'BABIP', 'batting_average_balls_in_play'],
      'ops_plus': ['ops_plus', 'OPS+', 'opsPlus', 'ops_plus_adjusted'],
      'fip': ['fip', 'FIP', 'fielding_independent_pitching'],
      'k_bb_pct': ['k_bb_pct', 'k-bb%', 'kMinusBbPct'],
      'fip_plus': ['fip_plus', 'FIP+', 'fipPlus'],
      'era_plus': ['era_plus', 'ERA+', 'eraPlus'],
      'xfip': ['xfip', 'xFIP'],
      'lob_pct': ['lob_pct', 'LOB%', 'lobPct', 'left_on_base_percentage'],
      'babip_against': ['babip_against', 'babipAgainst', 'BABIP_against'],
      'batting_average_against': ['baa', 'batting_average_against', 'battingAverageAgainst'],
      'hit_batters': ['hbp', 'hit_batters', 'hitBatters'],
    };

    const possibleKeys = fallbackMapping[column.key] || [column.key];
    for (const key of possibleKeys) {
      if (stats?.[key] !== undefined && stats?.[key] !== null) {
        return formatStat(stats[key], column.key);
      }
    }

    return formatStat(stats?.[column.key], column.key);
  };

  return (
    <div className="relative">
      <div className="flex items-center justify-end mb-2 relative">
        <button
          ref={buttonRef}
          onClick={() => setShowFilterModal(!showFilterModal)}
          className="flex items-center gap-2 px-3 py-1.5 bg-slate-700/50 hover:bg-slate-700 rounded-lg text-white text-xs transition-colors"
        >
          <span>Add/Subtract Filters</span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/>
          </svg>
        </button>

        {/* Filter Modal Dropdown */}
        <StatsFilterModal
          isOpen={showFilterModal}
          onClose={() => setShowFilterModal(false)}
          onApply={handleApplyFilters}
          buttonRef={buttonRef}
          profileService={scoutProfileService}
        />
      </div>

      <div className="overflow-x-auto max-h-[400px] overflow-y-auto rounded-lg border border-slate-700/50 custom-scrollbar">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-900 sticky top-0 z-10">
              {/* Fixed columns */}
              {fixedColumns.map((column, index) => (
                <th key={index} className={`px-3 py-2 text-center font-semibold text-cyan-400 uppercase tracking-wide whitespace-nowrap border-b border-slate-700/50${index === 0 ? ' sticky left-0 z-20 bg-slate-900 after:absolute after:right-0 after:top-0 after:bottom-0 after:w-px after:bg-slate-600/50' : ''}`}>
                  {column}
                </th>
              ))}
              {/* Dynamic stat columns */}
              {dynamicColumns.map((column, index) => (
                <th key={`stat-${index}`} className="px-3 py-2 text-center font-semibold text-cyan-400 uppercase tracking-wide whitespace-nowrap border-b border-slate-700/50">
                  {column.label}
                </th>
              ))}
              <th className="px-3 py-2 border-b border-slate-700/50 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {followedPlayers && followedPlayers.length > 0 ? (
              followedPlayers.map((player, index) => {
                const toTitleCase = (str) => str ? str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) : str;
                const playerName = toTitleCase(player.name || `${player.firstName || ''} ${player.lastName || ''}`.trim()) || 'N/A';
                const position = player.primaryPosition || player.position || 'N/A';
                const team = player.team?.name || player.teamName || player.team || 'N/A';
                const playerClass = normalizePlayerClass(player.playerClass || player.class) || 'N/A';

                return (
                  <tr key={player._id || index} className="hover:bg-slate-700/30 transition-colors border-b border-slate-700/30">
                    {/* Fixed columns */}
                    <td className="px-3 py-2 text-white font-medium whitespace-nowrap sticky left-0 z-[5] bg-slate-800 after:absolute after:right-0 after:top-0 after:bottom-0 after:w-px after:bg-slate-600/50">{playerName}</td>
                    <td className="px-3 py-2 text-gray-300 whitespace-nowrap">{position}</td>
                    <td className="px-3 py-2 text-gray-300 whitespace-nowrap italic">{team}</td>
                    <td className="px-3 py-2 text-gray-300 whitespace-nowrap uppercase">{playerClass}</td>

                    {/* Dynamic stat columns */}
                    {dynamicColumns.map((column, idx) => (
                      <td key={`stat-${idx}`} className="px-3 py-2 text-gray-400 text-center whitespace-nowrap">
                        {getStatValue(player, column)}
                      </td>
                    ))}

                    {/* Action button */}
                    <td className="px-3 py-2">
                      <button className="p-1.5 hover:bg-slate-600/50 rounded-lg transition-colors">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                        </svg>
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={allColumns.length + 1} className="px-3 py-8 text-center text-gray-400">
                  No followed players to display
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>


    </div>
  );
};
